import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/auth/LoginPage";
import ChangePasswordPage from "./pages/auth/ChangePasswordPage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import TicketListPage from "./pages/tickets/TicketListPage";
import TicketDetailPage from "./pages/tickets/TicketDetailPage";
import CreateTicketPage from "./pages/tickets/CreateTicketPage";
import UserManagementPage from "./pages/users/UserManagementPage";
import ReportsPage from "./pages/reports/ReportsPage";
import SLASettingsPage from "./pages/settings/SLASettingsPage";
import ChangePasswordSettingsPage from "./pages/settings/ChangePasswordSettingsPage";
import "@/App.css";

const ROLES_ADMIN = ["admin"];
const ROLES_ADMIN_MANAGER = ["admin", "manager"];

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FAFAFA]">
        <div className="text-sm text-[#52525B]">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.requires_password_change) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FAFAFA]">
        <div className="text-sm text-[#52525B]">Loading...</div>
      </div>
    );
  }
  if (user && !user.requires_password_change) return <Navigate to="/dashboard" replace />;
  if (user && user.requires_password_change) return <Navigate to="/change-password" replace />;
  return children;
}

function PasswordChangeRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FAFAFA]">
        <div className="text-sm text-[#52525B]">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.requires_password_change) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/change-password" element={<PasswordChangeRoute><ChangePasswordPage /></PasswordChangeRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><TicketListPage /></ProtectedRoute>} />
      <Route path="/tickets/new" element={<ProtectedRoute><CreateTicketPage /></ProtectedRoute>} />
      <Route path="/tickets/:id" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute roles={ROLES_ADMIN}><UserManagementPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute roles={ROLES_ADMIN_MANAGER}><ReportsPage /></ProtectedRoute>} />
      <Route path="/settings/sla" element={<ProtectedRoute roles={ROLES_ADMIN}><SLASettingsPage /></ProtectedRoute>} />
      <Route path="/settings/change-password" element={<ProtectedRoute><ChangePasswordSettingsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
